import React from 'react';
import { Grid, TextField, Typography, Box, Divider } from '@mui/material';
import MuiPhoneNumber from 'material-ui-phone-number';
import { FormValidationState, FormErrorState } from '../types/candidate.types';
import {
  LabelStyle,
  TextFieldStyle,
  sectionHeaderStyles,
  sectionTitleBoxStyles,
  errorTextStyles,
  phoneFieldStyles,
  multilineTextFieldStyles,
  sectionIconStyles,
  sectionTitleStyles
} from '../styles/CandidateForm.styles';
// Import the validator
import { validatePhoneNumberWithCountry } from '../utills/validation.utils';
import PhoneIcon from '@mui/icons-material/Phone';

interface ContactDetailsProps {
  register: any;
  isViewMode: boolean;
  isCreateEmsUserMode?: boolean;
  isCreateMode: boolean;
  firstPageValidation: FormValidationState;
  setFirstPageValidation: (
    value:
      | FormValidationState
      | ((prev: FormValidationState) => FormValidationState)
  ) => void;
  secondPageValidation: FormValidationState;
  setSecondPageValidation: (
    value:
      | FormValidationState
      | ((prev: FormValidationState) => FormValidationState)
  ) => void;
  firstErrors: FormErrorState;
  secondErrors: FormErrorState;
  clearError: (section: 'first' | 'second' | 'third', errorKey: string) => void;
}

const ContactDetails: React.FC<ContactDetailsProps> = ({
  register,
  isViewMode,
  isCreateEmsUserMode = false,
  isCreateMode,
  firstPageValidation,
  setFirstPageValidation,
  secondPageValidation,
  setSecondPageValidation,
  firstErrors,
  secondErrors,
  clearError
}) => {
  // Helper function to validate address fields
  const validateAddress = (value: string, fieldName: string) => {
    if (isCreateEmsUserMode && (!value || value.trim() === '')) {
      return `${fieldName} is required`;
    }
    return null;
  };

  // Helper function to ensure phone number has + format
  const ensureInternationalFormat = (value: string): string => {
    if (!value) return '';
    
    // If it already starts with +, return as is
    if (value.startsWith('+')) return value;
    
    // If it starts with 00 (common international format), replace with +
    if (value.startsWith('00')) {
      return '+' + value.substring(2);
    }
    
    // For numbers without country code, let MuiPhoneNumber handle formatting
    return value;
  };

  // Format phone number for display - ensure it has +
  const formatPhoneForDisplay = (phone: string): string => {
    if (!phone) return '';
    return phone.startsWith('+') ? phone : `+${phone}`;
  };

  const getOfficialEmailValue = () => {
    if (isCreateMode) {
      // Create mode: auto-generate based on current firstName and lastName
      // Both fields must have values, otherwise return empty
      if (
        firstPageValidation.firstName?.trim() &&
        firstPageValidation.lastName?.trim()
      ) {
        return `${firstPageValidation.firstName
          .toLowerCase()
          .replace(/\s+/g, '')}.${firstPageValidation.lastName
          .toLowerCase()
          .replace(/\s+/g, '')}@digiratina.com`;
      }
      return '';
    } else {
      // View/Edit mode: show original value or empty for placeholder effect
      return firstPageValidation.officialEmail || '';
    }
  };

  const getOfficialEmailPlaceholder = () => {
    if (isCreateMode) {
      return 'Auto filled with @digiratina.com';
    } else {
      // Show "hasn't setup yet" message as placeholder when no official email exists
      return firstPageValidation.officialEmail
        ? ''
        : "Official email hasn't setup yet";
    }
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mb: 3,
          pb: 1.5
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 32,
              height: 32,
              borderRadius: '8px',
              backgroundColor: '#e3f2fd',
              color: '#1565c0'
            }}
          >
            <PhoneIcon fontSize="small" />
          </Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: '#1565c0',
              fontSize: '1.1rem'
            }}
          >
            Contact Details
          </Typography>
        </Box>
        <Divider
          sx={{
            flex: 1,
            borderColor: '#1565c0',
            borderWidth: '0.5px'
          }}
        />
      </Box>

      <Grid container spacing={{ xs: 1, sm: 1.5, md: 2 }}>
        <Grid item xs={12} sm={6}>
          <Typography sx={LabelStyle} fontWeight="light" variant="h5">
            Personal Email Address
            {isCreateEmsUserMode && <span style={{ color: 'red' }}>*</span>}
          </Typography>
          <TextField
            size="small"
            {...register('personalEmail')}
            sx={TextFieldStyle}
            fullWidth
            placeholder="Enter Personal Email"
            type="email"
            variant="outlined"
            disabled={isViewMode}
            value={firstPageValidation.personalEmail || ''}
            onChange={(e) => {
              setFirstPageValidation((prev) => ({
                ...prev,
                personalEmail: e.target.value
              }));
              if (firstErrors?.personalEmailError) {
                clearError('first', 'personalEmailError');
              }
            }}
          />
          {firstErrors?.personalEmailError && (
            <Typography sx={errorTextStyles} color="red">
              {firstErrors?.personalEmailError}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} sm={6}>
          <Typography sx={LabelStyle} fontWeight="light" variant="h5">
            Official Email Address
            {isCreateEmsUserMode && <span style={{ color: 'red' }}>*</span>}
          </Typography>
          <TextField
            size="small"
            {...register('officialEmail')}
            sx={{
              ...TextFieldStyle,
              '& .MuiInputBase-root': {
                ...TextFieldStyle['& .MuiInputBase-root'],
                backgroundColor: isViewMode ? '#f5f5f5' : '#f9f9f9'
              },
              '& .MuiInputBase-input::placeholder': {
                color:
                  !isCreateMode && !firstPageValidation.officialEmail
                    ? '#999'
                    : 'inherit',
                opacity: 1,
                fontStyle: 'normal'
              }
            }}
            fullWidth
            placeholder={getOfficialEmailPlaceholder()}
            type="email"
            variant="outlined"
            InputProps={{ readOnly: true }}
            value={getOfficialEmailValue()}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Typography sx={LabelStyle} fontWeight="light" variant="h5">
            Mobile Number <span style={{ color: 'red' }}>*</span>
          </Typography>
          <MuiPhoneNumber
            defaultCountry={'lk'}
            size="small"
            id="mobileNumber"
            {...register('mobileNumber')}
            sx={phoneFieldStyles}
            fullWidth
            type="text"
            placeholder="Enter Mobile Number"
            variant="outlined"
            disabled={isViewMode}
            disableDropdown={isViewMode}
            value={formatPhoneForDisplay(secondPageValidation.mobileNumber || '')}
            onChange={(value, country, e, formattedValue) => {
              const countryCode = country?.countryCode || 'lk';
              const dialCode = country?.dialCode || '';

              // Clean the value by removing spaces, dashes, parentheses but keep +
              const cleanedValue = value.replace(/[\s\-\(\)]/g, '');

              // Check if the cleaned value only contains the dial code (no actual phone number)
              const hasOnlyDialCode = cleanedValue === `+${dialCode}`;

              // Ensure international format with +
              const internationalValue = ensureInternationalFormat(
                hasOnlyDialCode ? '' : value
              );

              // Store the formatted value and country (or empty if only dial code)
              setSecondPageValidation((prev) => ({
                ...prev,
                mobileNumber: internationalValue,
                selectedCountry: hasOnlyDialCode ? '' : countryCode
              }));

              // Clear any existing errors first
              if (secondErrors?.mobileNumberError) {
                clearError('second', 'mobileNumberError');
              }

              // Only validate if there are actual digits beyond the dial code
              if (!hasOnlyDialCode && cleanedValue.length > dialCode.length + 1) {
                // Validate the phone number
                const validationResult = validatePhoneNumberWithCountry(
                  internationalValue,
                  countryCode
                );

                if (!validationResult.isValid) {
                  setSecondPageValidation((prev) => ({
                    ...prev,
                    mobileNumberError: validationResult.errorMessage
                  }));
                } else {
                  // Clear error if validation passes
                  setSecondPageValidation((prev) => ({
                    ...prev,
                    mobileNumberError: ''
                  }));
                }
              } else {
                setSecondPageValidation((prev) => ({
                  ...prev,
                  mobileNumberError: ''
                }));
              }
            }}
            preferredCountries={['lk']}
            enableSearch={true}
            disableAreaCodes={false}
            countryCodeEditable={false}
            autoFormat={true}
            error={!!secondErrors?.mobileNumberError}
            inputProps={{
              name: 'mobileNumber',
              autoComplete: 'tel'
            }}
          />
          {secondErrors?.mobileNumberError && (
            <Typography sx={errorTextStyles} color="red">
              {secondErrors?.mobileNumberError}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} sm={6}>
          <Typography sx={LabelStyle} fontWeight="light" variant="h5">
            Emergency Contact Number
            {isCreateEmsUserMode && <span style={{ color: 'red' }}>*</span>}
          </Typography>
          <MuiPhoneNumber
            defaultCountry={'lk'}
            size="small"
            id="emergencyContactNumber"
            {...register('emergencyContactNumber', {})}
            sx={phoneFieldStyles}
            fullWidth
            type="text"
            placeholder="Enter Emergency Contact Number"
            variant="outlined"
            disabled={isViewMode}
            disableDropdown={isViewMode}
            value={formatPhoneForDisplay(secondPageValidation.emergencyContactNumber || '')}
            onChange={(value, country, e, formattedValue) => {
              const countryCode = country?.countryCode || 'lk';
              const dialCode = country?.dialCode || '';

              // Clean the value by removing spaces, dashes, parentheses but keep +
              const cleanedValue = value.replace(/[\s\-\(\)]/g, '');

              // Check if the cleaned value only contains the dial code (no actual phone number)
              const hasOnlyDialCode = cleanedValue === `+${dialCode}`;

              // Ensure international format with +
              const internationalValue = ensureInternationalFormat(
                hasOnlyDialCode ? '' : value
              );

              // Store the formatted value and country (or empty if only dial code)
              setSecondPageValidation((prev) => ({
                ...prev,
                emergencyContactNumber: internationalValue,
                emergencyContactCountry: hasOnlyDialCode ? '' : countryCode
              }));

              // Clear any existing errors first
              if (secondErrors?.emergencyContactNumberError) {
                clearError('second', 'emergencyContactNumberError');
              }

              // Only validate if there are actual digits beyond the dial code
              if (!hasOnlyDialCode && cleanedValue.length > dialCode.length + 1) {
                // Validate the phone number
                const validationResult = validatePhoneNumberWithCountry(
                  internationalValue,
                  countryCode
                );

                if (!validationResult.isValid) {
                  setSecondPageValidation((prev) => ({
                    ...prev,
                    emergencyContactNumberError: validationResult.errorMessage
                  }));
                } else {
                  // Clear error if validation passes
                  setSecondPageValidation((prev) => ({
                    ...prev,
                    emergencyContactNumberError: ''
                  }));
                }
              } else {
                // Field is empty or only has dial code - clear any validation errors
                setSecondPageValidation((prev) => ({
                  ...prev,
                  emergencyContactNumberError: ''
                }));
              }
            }}
            preferredCountries={['lk']}
            enableSearch={true}
            disableAreaCodes={false}
            countryCodeEditable={false}
            autoFormat={true}
            error={!!secondErrors?.emergencyContactNumberError}
            inputProps={{
              name: 'emergencyContactNumber',
              autoComplete: 'tel'
            }}
          />
          {secondErrors?.emergencyContactNumberError && (
            <Typography sx={errorTextStyles} color="red">
              {secondErrors?.emergencyContactNumberError}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} sm={6}>
          <Typography sx={LabelStyle} fontWeight="light" variant="h5">
            Permanent Address{' '}
            {isCreateEmsUserMode && <span style={{ color: 'red' }}>*</span>}
          </Typography>
          <TextField
            size="small"
            {...register('permanentAddress')}
            sx={{
              ...multilineTextFieldStyles,
              '& .MuiInputBase-root': {
                ...multilineTextFieldStyles['& .MuiInputBase-root'],
                alignItems: 'flex-start',
                padding: '8.5px 14px'
              },
              '& .MuiInputBase-input': {
                padding: 0
              }
            }}
            fullWidth
            multiline
            minRows={1}
            maxRows={6}
            placeholder="Enter Permanent Address"
            variant="outlined"
            disabled={isViewMode}
            value={secondPageValidation.permanentAddress || ''}
            onChange={(e) => {
              const newValue = e.target.value;

              // Enforce character limit
              if (newValue.length <= 300) {
                setSecondPageValidation((prev) => ({
                  ...prev,
                  permanentAddress: newValue
                }));

                // Clear error when user starts typing
                if (secondErrors?.permanentAddressError) {
                  clearError('second', 'permanentAddressError');
                }
              }
            }}
            onBlur={(e) => {
              // Only validate on blur if in createEmsUserMode
              if (isCreateEmsUserMode) {
                const trimmedValue = e.target.value.trim();
                const validationError = validateAddress(
                  trimmedValue,
                  'Permanent Address'
                );

                if (validationError) {
                  setSecondPageValidation((prev) => ({
                    ...prev,
                    permanentAddressError: validationError
                  }));
                } else {
                  if (secondErrors?.permanentAddressError) {
                    clearError('second', 'permanentAddressError');
                  }
                }
              }
            }}
            error={!!secondErrors?.permanentAddressError}
            helperText={
              <Typography
                component="span"
                sx={{
                  fontSize: '0.75rem',
                  display: 'flex',
                  justifyContent: 'space-between'
                }}
              >
                <span style={{ color: 'red' }}>
                  {secondErrors?.permanentAddressError || ''}
                </span>
                <span
                  style={{
                    color:
                      secondPageValidation.permanentAddress?.length > 250
                        ? 'orange'
                        : '#666'
                  }}
                >
                  {secondPageValidation.permanentAddress?.length || 0}/300
                </span>
              </Typography>
            }
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Typography sx={LabelStyle} fontWeight="light" variant="h5">
            Current Address{' '}
            {isCreateEmsUserMode && <span style={{ color: 'red' }}>*</span>}
          </Typography>
          <TextField
            size="small"
            {...register('currentAddress')}
            sx={{
              ...multilineTextFieldStyles,
              '& .MuiInputBase-root': {
                ...multilineTextFieldStyles['& .MuiInputBase-root'],
                alignItems: 'flex-start',
                padding: '8.5px 14px'
              },
              '& .MuiInputBase-input': {
                padding: 0
              }
            }}
            fullWidth
            multiline
            minRows={1}
            maxRows={6}
            placeholder="Enter Current Address"
            variant="outlined"
            disabled={isViewMode}
            value={secondPageValidation.currentAddress || ''}
            onChange={(e) => {
              const newValue = e.target.value;

              // Enforce character limit
              if (newValue.length <= 300) {
                setSecondPageValidation((prev) => ({
                  ...prev,
                  currentAddress: newValue
                }));

                // Clear error when user starts typing
                if (secondErrors?.currentAddressError) {
                  clearError('second', 'currentAddressError');
                }
              }
            }}
            onBlur={(e) => {
              // Only validate on blur if in createEmsUserMode
              if (isCreateEmsUserMode) {
                const trimmedValue = e.target.value.trim();
                const validationError = validateAddress(
                  trimmedValue,
                  'Current Address'
                );

                if (validationError) {
                  setSecondPageValidation((prev) => ({
                    ...prev,
                    currentAddressError: validationError
                  }));
                } else {
                  if (secondErrors?.currentAddressError) {
                    clearError('second', 'currentAddressError');
                  }
                }
              }
            }}
            error={!!secondErrors?.currentAddressError}
            helperText={
              <Typography
                component="span"
                sx={{
                  fontSize: '0.75rem',
                  display: 'flex',
                  justifyContent: 'space-between'
                }}
              >
                <span style={{ color: 'red' }}>
                  {secondErrors?.currentAddressError || ''}
                </span>
                <span
                  style={{
                    color:
                      secondPageValidation.currentAddress?.length > 250
                        ? 'orange'
                        : '#666'
                  }}
                >
                  {secondPageValidation.currentAddress?.length || 0}/300
                </span>
              </Typography>
            }
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography sx={LabelStyle} fontWeight="light" variant="h5">
            Emergency Contact Person
            {isCreateEmsUserMode && <span style={{ color: 'red' }}>*</span>}
          </Typography>
          <TextField
            size="small"
            {...register('emergencyContactName')}
            sx={TextFieldStyle}
            fullWidth
            placeholder="Enter Emergency Contact Person"
            variant="outlined"
            disabled={isViewMode}
            value={secondPageValidation.emergencyContactName || ''}
            onChange={(e) => {
              if (e.target.value.length <= 50) {
                setSecondPageValidation((prev) => ({
                  ...prev,
                  emergencyContactName: e.target.value
                }));
                if (secondErrors?.emergencyContactNameError) {
                  clearError('second', 'emergencyContactNameError');
                }
              }
            }}
          />
          {secondErrors?.emergencyContactNameError && (
            <Typography sx={errorTextStyles} color="red">
              {secondErrors?.emergencyContactNameError}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} sm={6}>
          <Typography sx={LabelStyle} fontWeight="light" variant="h5">
            Emergency Contact Relationship
            {isCreateEmsUserMode && <span style={{ color: 'red' }}>*</span>}
          </Typography>
          <TextField
            size="small"
            {...register('emergencyContactRelationship')}
            sx={TextFieldStyle}
            fullWidth
            placeholder="Enter Contact Relationship"
            variant="outlined"
            disabled={isViewMode}
            value={secondPageValidation.emergencyContactRelationship || ''}
            onChange={(e) => {
              if (e.target.value.length <= 30) {
                setSecondPageValidation((prev) => ({
                  ...prev,
                  emergencyContactRelationship: e.target.value
                }));
                if (secondErrors?.emergencyContactRelationshipError) {
                  clearError('second', 'emergencyContactRelationshipError');
                }
              }
            }}
          />
          {secondErrors?.emergencyContactRelationshipError && (
            <Typography sx={errorTextStyles} color="red">
              {secondErrors?.emergencyContactRelationshipError}
            </Typography>
          )}
        </Grid>
      </Grid>
    </>
  );
};

export default ContactDetails;